import { useCallback, useEffect, useRef, useState } from "react";
import type { CreativeSubject } from "@digital-twins/shared-types";
import { creativeLibraryApi } from "@/lib/api/creative-library";
import type { BrandSubjectsList } from "@/types/creative-library";

/**
 * One brand's models. `ready` is true once this brand's list has loaded, and it
 * stays true while the same brand refreshes: the list clears only when the brand
 * changes, so a just-created model never reads as unavailable.
 */
export function useBrandSubjects(
  brandToken?: string,
  refreshKey?: string,
): BrandSubjectsList {
  const [subjects, setSubjects] = useState<CreativeSubject[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [loadedBrand, setLoadedBrand] = useState<string | null>(null);
  const [retries, setRetries] = useState(0);
  const listBrand = useRef<string | undefined>(undefined);
  const hasList = useRef(false);
  // Added since the running request started, so its answer may not include them.
  const addedDuringRequest = useRef<CreativeSubject[]>([]);
  useEffect(() => {
    let active = true;
    if (listBrand.current !== brandToken) {
      listBrand.current = brandToken;
      hasList.current = false;
      setSubjects([]);
      setLoadedBrand(null);
    }
    addedDuringRequest.current = [];
    setError(false);
    if (!brandToken) {
      setLoading(false);
      return;
    }
    setLoading(!hasList.current);
    void creativeLibraryApi
      .subjects(brandToken)
      .then((items) => {
        if (!active) return;
        const added = addedDuringRequest.current.filter(
          (subject) => !items.some((item) => item.token === subject.token),
        );
        setSubjects([...items, ...added]);
        hasList.current = true;
        setLoadedBrand(brandToken);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [brandToken, refreshKey, retries]);
  const retry = useCallback(() => setRetries((count) => count + 1), []);
  const add = useCallback((subject: CreativeSubject) => {
    addedDuringRequest.current = [...addedDuringRequest.current, subject];
    setSubjects((current) =>
      current.some((item) => item.token === subject.token)
        ? current
        : [...current, subject],
    );
  }, []);
  return {
    subjects,
    loading,
    error,
    ready: Boolean(brandToken) && loadedBrand === brandToken && !loading,
    retry,
    add,
  };
}
